import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import Field from "../../../../DesignSystem/Inputs/Field";
import Divider from "../../../../DesignSystem/Layout/Divider";
import { PostCard } from "../../../Tabs/HelperFunctions/HelperFunctions";
import {
  createEmptyCpdRecord,
  formatCpdHours,
} from "../utils/cpdRecords";

const hoursToMinutes = (value) => {
  const hours = Number(value);
  return Number.isFinite(hours) ? Math.round(hours * 60) : 0;
};

const createForm = (reportType, saved = {}) => ({
  reportType,
  reportDate: saved.reportDate || "",
  sourceType: saved.sourceType || "",
  importedBy: saved.importedBy || "",
  importedByName: saved.importedByName || "",
  updatedBy: saved.updatedBy || "",
  updatedByName: saved.updatedByName || "",
  savedRecord: saved,
  cpdEntries: String(saved.cpdEntries ?? 0),
  cpdHours: saved.cpdMinutes != null ? formatCpdHours(saved.cpdMinutes) : "0",
  structuredEntries: String(saved.structuredEntries ?? 0),
  structuredHours:
    saved.structuredMinutes != null
      ? formatCpdHours(saved.structuredMinutes)
      : "0",
  nonStructuredEntries: String(saved.nonStructuredEntries ?? 0),
  nonStructuredHours:
    saved.nonStructuredMinutes != null
      ? formatCpdHours(saved.nonStructuredMinutes)
      : "0",
});

const createForms = (savedCpd = {}) => ({
  all: createForm("all", savedCpd.all),
  idd: createForm("idd", savedCpd.idd),
});

const CpdFormSection = ({ form, title, onChange }) => {
  const updatedBy =
    form.updatedByName ||
    form.updatedBy ||
    form.importedByName ||
    form.importedBy ||
    "-";

  return (
    <section>
      <h3 className="FP_Content_Semibold">{title}</h3>
      <div className="smGrid2">
        <Field
          label="Report date"
          type="date"
          value={form.reportDate}
          onChange={() => {}}
          readOnly
        />
        {/* <Field
          label="Uploaded/ Updated by"
          value={updatedBy}
          onChange={() => {}}
          readOnly
        />
        <Field
          label="CPD entries"
          type="number"
          min="0"
          value={form.cpdEntries}
          onChange={(value) => onChange("cpdEntries", value)}
        /> */}
        <Field
          label={form.reportType === "idd" ? "IDD hours" : "Total CPD hours"}
          type="number"
          min="0"
          step="0.01"
          value={form.cpdHours}
          onChange={(value) => onChange("cpdHours", value)}
        />
        <Field
          label="Structured entries"
          type="number"
          min="0"
          value={form.structuredEntries}
          onChange={(value) => onChange("structuredEntries", value)}
        />
        <Field
          label="Structured hours"
          type="number"
          min="0"
          step="0.01"
          value={form.structuredHours}
          onChange={(value) => onChange("structuredHours", value)}
        />
        <Field
          label="Non-structured entries"
          type="number"
          min="0"
          value={form.nonStructuredEntries}
          onChange={(value) => onChange("nonStructuredEntries", value)}
        />
        <Field
          label="Non-structured hours"
          type="number"
          min="0"
          step="0.01"
          value={form.nonStructuredHours}
          onChange={(value) => onChange("nonStructuredHours", value)}
        />
      </div>
    </section>
  );
};

const CpdRecordEditor = forwardRef(function CpdRecordEditor(
  { adviserId, adviserEmail, details },
  ref,
) {
  const [forms, setForms] = useState(() =>
    createForms(details?.licenseCertificates?.cpd),
  );

  useEffect(() => {
    setForms(createForms(details?.licenseCertificates?.cpd));
  }, [details]);

  const update = (reportType, key, value) =>
    setForms((current) => ({
      ...current,
      [reportType]: {
        ...current[reportType],
        [key]: value,
      },
    }));

  const createRecord = useCallback((form) => {
    const personal = details?.personalDetails || {};

    return {
      ...createEmptyCpdRecord({
        reportType: form.reportType,
        reportDate: form.reportDate,
      }),
      ...(form.savedRecord || {}),
      reportType: form.reportType,
      reportDate: form.reportDate,
      firstName: personal.forename || form.savedRecord?.firstName || "",
      lastName: personal.surname || form.savedRecord?.lastName || "",
      email:
        adviserEmail ||
        details?.adviserDoc?.email ||
        details?.email ||
        form.savedRecord?.email ||
        adviserId,
      cpdEntries: Number(form.cpdEntries) || 0,
      cpdMinutes: hoursToMinutes(form.cpdHours),
      structuredEntries: Number(form.structuredEntries) || 0,
      structuredMinutes: hoursToMinutes(form.structuredHours),
      nonStructuredEntries: Number(form.nonStructuredEntries) || 0,
      nonStructuredMinutes: hoursToMinutes(form.nonStructuredHours),
    };
  }, [adviserEmail, adviserId, details]);

  useImperativeHandle(
    ref,
    () => ({
      getRecords: () =>
        Object.fromEntries(
          Object.entries(forms)
            .filter(([, form]) => Boolean(form.reportDate))
            .map(([reportType, form]) => [reportType, createRecord(form)]),
        ),
    }),
    [forms, createRecord],
  );

  return (
    <PostCard title="CPD Records">
      {/* <p className="Content-Small muted">
        Update this adviser's All CPD and IDD CPD records. The report date and
        uploader are retained from the uploaded reports.
      </p> */}

      <CpdFormSection
        title="All CPD"
        form={forms.all}
        onChange={(key, value) => update("all", key, value)}
      />
      <Divider />
      <CpdFormSection
        title="IDD"
        form={forms.idd}
        onChange={(key, value) => update("idd", key, value)}
      />
    </PostCard>
  );
});

export default CpdRecordEditor;
